<template>
  <div>
    <!-- ABOUT -->
    <div class="about md:flex justify-between">
      <div class="w-full md:w-1/2 md:mr-8">
        <h2 class="mb-10">
          <span class="font-bold">About</span><br>
          Connections Text
        </h2>
        <div class="block md:hidden">
          <img class="ml-auto" src="../assets/images/about-mobile-min.jpg" alt="Uniting hands"/>
        </div>
        <p class="my-10">
          Connections Text is a service from CHESS Health to help individuals whose use of drugs and/or 
          alcohol indicates they would benefit from engagement and a referral to appropriate community services. 
          CHESS Health's team of certified peer recovery support specialists use text messaging to engage with an individual, 
          share their own story of addiction and successful recovery, employ motivational interviewing and active listening, and 
          leverage deep knowledge of the local community, to make one or more referrals on the individual's behalf to appropriate 
          services. (Voice calls may be used upon request.)
        </p>
        <p class="my-7">
          Connections Text service works best when CHESS initiates the messaging to the individual on their phone. 
          The CHESS team will be able to track the outcome of any referrals through our <a href="https://eint.chesshealth.net/" target="_blank">eIntervention platform</a>.
        </p>
        <Button @clicked="$emit('scrollToForm')" :className="'mt-8 md:mt-0'" :text="'Make the Connection'" />
      </div>
      <div class="hidden md:block w-1/2 ml-8">
        <img class="ml-auto" src="../assets/images/about-min.jpg" />
      </div>
    </div>
    <!-- end ABOUT -->

    <!-- FORM -->
    <div id="form" class="form md:flex">
      <div class="flex items-start justify-center flex-col w-full md:w-1/2 md:pr-16">
        <h2 class="mb-8 md:mb-10">
          <span class="font-bold">To start,</span><br>
          <span class="font-bold">CHESS needs</span>
        </h2>
        <p class="mb-10 md:mb-0">
          to know the individual has given consent and is expecting a text message from our peers. To do so, complete this secure form:
        </p>
      </div>
      <div class="form-container w-full md:w-1/2 flex flex-wrap text-left max-w-screen-sm py-4">
        <div class="w-full text-center unavailable mb-4" v-if="!validSubdomain()">Unavailable - See note below</div>
        <div class="w-full text-center disclaimer">Secure Form for Referral to Connections Text</div>
        <div class="my-4" :class="formData[field].className || 'w-full'" v-for="field in fields" :key="field">
          <div class="flex">
            <label :class="formData[field].required ? 'required' : ''">
              {{ formData[field].label }}
            </label>
          </div>
          <!-- Text -->
          <input
            v-if="formData[field].type === 'text'"
            class="w-full"
            :name="formData[field].key"
            type="text"
            v-model="formData[field].value"
          />
          <!-- Text Area -->
          <textarea
            v-if="formData[field].type === 'textarea'"
            class="w-full"
            rows="3"
            :name="formData[field].key"
            v-model="formData[field].value"
          ></textarea>
          <!-- Phone -->
          <input
            v-if="formData[field].type === 'phone'"
            class="w-full"
            :name="formData[field].key"
            :placeholder="placeholderNumber"
            v-maska="phoneNumberMask"
            type="text"
            @change="changePhone(field)"
            v-model="phoneNumber"
          />
        </div>
        <p class="mb-10 md:mb-0 unavailable" v-if="!validSubdomain()">
          The ConnectionsText service in Oklahoma has been discontinued per the terms of the agreement with the Department of Health.
          CHESS is unable to offer this service to additional residents at this time. For questions, please email info@chess.health (do not email patient details)
        </p>
        <div class="w-full text-center md:text-right mb-4" v-if="validSubdomain()">
          <Button @clicked="submitForm" :className="'w-40 mt-10'" :text="'Submit'" />
        </div>
        <LoadingUI msg="Submitting..." v-if="loading" />
      </div>
    </div>
    <!-- end FORM -->

    <!-- TESTIMONIALS -->
    <!-- end TESTIMONIALS -->

    <!-- CONTACT -->
    <div class="contact md:flex justify-around w-full text-center">
      <div class="mb-11 md:mb-0">
        <img class="m-auto mb-6" src="../assets/images/phone.png" alt="phone icon" />
        <p class="label mb-4">Phone</p>
        <p>844-MY-CHESS</p>
      </div>
      <div class="mb-11 md:mb-0">
        <img class="m-auto mb-6" src="../assets/images/address.png" alt="address icon" />
        <p class="label mb-4">Address</p>
        <p>333 West Commercial Street</p>
        <p>Suite 2500</p>
        <p>East Rochester, NY 14445</p>
      </div>
      <div>
        <img class="m-auto mb-6" src="../assets/images/email.png" alt="email icon" />
        <p class="label mb-4">Email</p>
        <p>info@chess.health</p>
      </div>
    </div>
    <!-- end CONTACT -->
    <modals-container></modals-container>
  </div>
</template>

<script>
import LoadingUI from '../components/LoadingUI.vue'
import SuccessModal from '../components/SuccessModal.vue'
import Button from '../components/Button.vue'
import { ModalsContainer } from 'vue-final-modal'
import { createTextPatient, accepted_subdomains } from '../services/connections-text-service'

export default {
  name: 'Home',
  components: { // imported components
    LoadingUI,
    ModalsContainer,
    Button
  },
  props: { // props passed into component
    //msg: String
  },
  data() { // component props
    return {
      loading: false,
      modalOpen: false,
      formData: {},
      fields: [],
      phoneNumber: '',
      placeholderNumber: '(123)-456-7890',
      phoneNumberMask: '(###)-###-####'
    }
  },
  created() { /* Called synchronously after the instance is created */
    this.resetFormData();
  },
  mounted() { /* Called after the instance has been mounted */ },
  methods: { // component functions
    validSubdomain() {
      return accepted_subdomains.includes(window.location.host.split('.')[0]);
    },
    submitForm() {
      let errorMessage = 'There was a problem submitting the form.<br>Please contact support@chess.health for assistance.';
      let invalid = this.invalidFields();
      if (invalid.length) {
        // build error message
        let message = '';
        invalid.forEach(field => {
          message += `- ${this.formData[field].label} is invalid<br>`;
        });
        this.$toast.error(message);
      } else {
        this.loading = true;
        // create request body
        let data = this.requestData();
        createTextPatient(data, window.location.host.split('.')[0]).then(res => {
          if (res.success) {
            this.loading = false;
            // show success modal
            this.$vfm.show({ component: SuccessModal });
            // reset form data fields
            this.resetFormData();
          } else {
            this.loading = false;
            this.$toast.error(errorMessage);
          }
        }).catch(err => {
          console.error(err);
          this.loading = false;
          this.$toast.error(errorMessage);
        });
      }
    },
    resetFormData() {
      this.formData = {
        sender_name: { key: 'sender_name', label: 'Your Name', value: '', type: 'text', required: true, className: 'w-full md:w-1/2 md:pr-2', order: 1 },
        source: { key: 'source', label: 'Your Organization', value: '', type: 'text', required: true, className: 'w-full md:w-1/2 md:pl-2', order: 2 },
        name: { key: 'name', label: 'Individual\'s Name', value: '', type: 'text', required: true, className: 'w-full md:w-1/2 md:pr-2', order: 3 },
        phone_number: { key: 'phone_number', label: 'Individual\'s Mobile Number', value: '', type: 'phone', className: 'w-full md:w-1/2 md:pl-2', required: true, order: 4 },
        initial_notes: { key: 'initial_notes', label: 'Optional Comments', value: '', type: 'textarea', required: false, order: 5 }
      };
      this.phoneNumber = '';
      // get and sort keys
      let fields = [];
      let keys = Object.keys(this.formData);
      keys.forEach(key => {
        fields.push({
          key: this.formData[key].key,
          order: this.formData[key].order
        });
      });
      this.fields = fields.sort(function (a, b) {
        if (a.order < b.order) { return -1; }
        if (a.order > b.order) { return 1; }
        return 0;
      }).map(field => field.key);
    },
    requestData() {
      let obj = {};
      let keys = Object.keys(this.formData);
      let values = Object.values(this.formData);
      values.forEach((v, i) => {
        obj[keys[i]] = v.value;
      });
      return obj;
    },
    invalidFields() {
      let fields = [];
      let keys = Object.keys(this.formData);
      let values = Object.values(this.formData);
      let empty = values.map((v, i) => { 
        return { value: v.value, index: i, required: v.required }
      }).filter(v => !v.value && v.required);
      if (empty.length > 0) {
        empty.forEach(e => {
          let key = keys[e.index];
          fields.push(key);
        });
      }
      return fields;
    },
    changePhone(field) {
      if (this.validPhoneNumber()) {
        this.formData[field].value = this.phoneNumber;
      } else {
        this.formData[field].value = '';
      }
    },
    validPhoneNumber() {
      // get all numbers in a string
      var numbers = this.phoneNumber.toString().trim().replace(/\D/g, "");
      if (numbers.length === 10) {
        return true;
      }
      return false;
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
  h2 {
    color: #022169;
    font-weight: 500;
    font-size: 56px;
    line-height: 64px;
    letter-spacing: -0.05em;
  }
  p {
    font-weight: 400;
    font-size: 16px;
    line-height: 32px;
    letter-spacing: -0.03em;
    color: #5B5C90;
  }
  .unavailable{
    color: red;
    font-size: 15px;
  }
  .disclaimer {
    color: #022169;
    font-weight: bold;
    margin: -10px 0 10px 0;
    font-size: 16px;
  }
  .about {
    padding: 100px 96px 90px;
  }
    .about img {
      max-height: 662px;
      box-shadow: 0px 42px 44px -25px rgba(2, 33, 105, 0.19);
      border-radius: 16px;
    }
  .form {
    padding: 100px 96px 90px;
    background: #F7FAFC;
  }
  .form-container {
    position: relative;
    background: #fff;
    box-shadow: 0px 27px 66px -34px rgba(0, 108, 162, 0.12);
    border-radius: 16px;
    padding: 48px 52px;
    margin: 0 0 0 auto;
  }
  .contact {
    padding: 100px 0 56px;
  }
    .contact .label {
      color: #022169;
      font-weight: 700;
    }
  label {
    display: block;
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: -0.05em;
    color: #5B5C90;
    text-align: left;
    margin-bottom: 8px;
  }
  label.required::after {
    content: " * ";
    color: red;
  }
  label .optional {
    color: #a0a0a0;
    font-size: 12px;
  }
  input,
  select,
  textarea {
    font-size: 16px;
    font-weight: 400;
    background: #fff;
    border: 1px solid #E8E6EE;
    box-sizing: border-box;
    border-radius: 8px;
    color: #333;
    height: 48px;
    padding: 6px 12px;
    line-height: 1.42857143;
    -webkit-transition: border-color ease-in-out 0.15s,
      -webkit-box-shadow ease-in-out 0.15s;
    -o-transition: border-color ease-in-out 0.15s, box-shadow ease-in-out 0.15s;
    transition: border-color ease-in-out 0.15s, box-shadow ease-in-out 0.15s;
  }
  textarea {
    height: auto;
  }
  input:focus,
  select:focus,
  textarea:focus {
    border-color: #66afe9;
    outline: 0;
    -webkit-box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%),
      0 0 8px rgb(102 175 233 / 60%);
    box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%), 0 0 8px rgb(102 175 233 / 60%);
  }
  input:disabled {
    cursor: not-allowed;
    background-color: rgba(216, 215, 215, 0.8);
  }
  .ios {
    -webkit-appearance: none;
    -moz-appearance: none;
  }

  @media(max-width: 768px) {
    h2 {
      font-size: 48px;
      line-height: 56px;
    }
    .about {
      padding: 56px 16px 32px;
    }
    .form {
      padding: 64px 16px 48px;
    }
    .form-container {
      padding: 32px 16px;
      margin: auto;
    }
    .contact {
      padding: 56px 0;
    }
  }
</style>
